import * as React from 'react';
import {
  Autocomplete,
  Box,
  FormControl,
  TextField,
  Typography,
  type AutocompleteRenderInputParams,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import { ReactComponent as Vector } from '../../../assets/PepWise2.0/arrow-down-dark.svg';

export type SearchableDropdownOption = {
  label: string;
  value: string | number;
  subLabel?: string;
  disabled?: boolean;
};

type SearchableDropdownProps = {
  label?: string;
  value: SearchableDropdownOption | null;
  setValue:
    | React.Dispatch<React.SetStateAction<SearchableDropdownOption | null>>
    | ((value: SearchableDropdownOption | null) => void);
  options: SearchableDropdownOption[];
  handleChange?: (value: SearchableDropdownOption | null) => void;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  boxProps?: any;
  width?: string | number;
  noOptionsText?: string;
  required?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  getOptionLabel?: (option: SearchableDropdownOption) => string;
  isOptionEqualToValue?: (
    option: SearchableDropdownOption,
    value: SearchableDropdownOption,
  ) => boolean;
};

const SearchableDropdown = ({
  label,
  value,
  setValue,
  options,
  handleChange,
  placeholder = 'Search and select',
  disabled = false,
  loading = false,
  boxProps,
  width = '100%',
  noOptionsText = 'No options found',
  required = false,
  error = false,
  helperText,
  getOptionLabel,
  isOptionEqualToValue,
}: SearchableDropdownProps) => {
  const theme = useTheme();
  const colors = theme.palette;
  const [inputValue, setInputValue] = React.useState('');

  const { sx, ...props } = boxProps || {};

  const resolvedGetOptionLabel = React.useCallback(
    (option: SearchableDropdownOption) => {
      if (getOptionLabel) return getOptionLabel(option);
      return option.subLabel ? `${option.label} (${option.subLabel})` : option.label;
    },
    [getOptionLabel],
  );

  const handleDropdownChange = (
    _: React.SyntheticEvent,
    newValue: SearchableDropdownOption | null,
  ) => {
    setValue(newValue);
    handleChange?.(newValue);
  };

  return (
    <Box sx={{ width, ...sx }} {...props}>
      <FormControl fullWidth error={error}>
        {label ? (
          <Typography
            sx={{
              ...theme.typography.dropdownText,
              mb: '6px',
              color: colors.DropdownV2?.menuItemText,
              lineHeight: '20px',
              fontWeight: 400,
            }}
          >
            {label}
            {required ? ' *' : ''}
          </Typography>
        ) : null}

        <Autocomplete<SearchableDropdownOption, false, false, false>
          value={value}
          onChange={handleDropdownChange}
          inputValue={inputValue}
          onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
          options={options}
          disabled={disabled}
          loading={loading}
          autoHighlight
          fullWidth
          noOptionsText={noOptionsText}
          popupIcon={<Vector />}
          getOptionLabel={resolvedGetOptionLabel}
          isOptionEqualToValue={
            isOptionEqualToValue ||
            ((option, selectedValue) => option.value === selectedValue.value)
          }
          getOptionDisabled={(option) => Boolean(option.disabled)}
          filterOptions={(dropdownOptions, state) => {
            const searchText = state.inputValue.trim().toLowerCase();

            if (!searchText) return dropdownOptions;

            return dropdownOptions.filter((option) => {
              const labelText = option.label?.toLowerCase() || '';
              const subLabelText = option.subLabel?.toLowerCase() || '';
              const valueText = String(option.value).toLowerCase();

              return (
                labelText.includes(searchText) ||
                subLabelText.includes(searchText) ||
                valueText.includes(searchText)
              );
            });
          }}
          slotProps={{
            paper: {
              sx: {
                mt: 1,
                overflow: 'hidden',
                boxShadow: '0px 8px 24px rgba(16, 24, 40, 0.12)',

                '& .MuiAutocomplete-listbox': {
                  p: 0,
                  backgroundColor: `${colors.DropdownV2?.menuItemBackground} !important`,
                  color: `${colors.DropdownV2?.menuItemText} !important`,
                },

                '& .MuiAutocomplete-option': {
                  ...theme.typography.dropdownText,
                  color: `${colors.DropdownV2?.menuItemText} !important`,
                  backgroundColor: `${colors.DropdownV2?.menuItemBackground} !important`,
                  padding: '10px 16px',
                  position: 'relative',
                  height: '50px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                  opacity: 1,
                },

                '& .MuiAutocomplete-noOptions': {
                  ...theme.typography.dropdownListText,
                  backgroundColor: `${colors.DropdownV2?.menuItemBackground} !important`,
                  color: `${colors.DropdownV2?.menuItemText} !important`,
                  padding: '10px 16px',
                  minHeight: '50px',
                  display: 'flex',
                  alignItems: 'center',
                  boxSizing: 'border-box',
                },

                '& .MuiAutocomplete-option:hover': {
                  backgroundColor: `${colors.DropdownV2?.menuItemHover} !important`,
                  color: `${colors.DropdownV2?.menuItemText} !important`,
                },

                '& .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option.Mui-focusVisible':
                  {
                    backgroundColor: `${colors.DropdownV2?.menuItemHover} !important`,
                    color: `${colors.DropdownV2?.menuItemText} !important`,
                  },

                '& .MuiAutocomplete-option[aria-selected="true"]': {
                  backgroundColor: `${colors.DropdownV2?.menuItemSelectedBg} !important`,
                  color: `${colors.DropdownV2?.menuItemSelectedText} !important`,
                  opacity: 1,
                },

                '& .MuiAutocomplete-option[aria-selected="true"]:hover, & .MuiAutocomplete-option[aria-selected="true"].Mui-focused, & .MuiAutocomplete-option[aria-selected="true"].Mui-focusVisible':
                  {
                    backgroundColor: `${colors.DropdownV2?.menuItemSelectedBg} !important`,
                    color: `${colors.DropdownV2?.menuItemSelectedText} !important`,
                    opacity: 1,
                  },

                '& .MuiAutocomplete-option .dropdown-option-text': {
                  color: `${colors.DropdownV2?.menuItemText} !important`,
                },

                '& .MuiAutocomplete-option:hover .dropdown-option-text, & .MuiAutocomplete-option.Mui-focused .dropdown-option-text, & .MuiAutocomplete-option.Mui-focusVisible .dropdown-option-text':
                  {
                    color: `${colors.DropdownV2?.menuItemText} !important`,
                  },

                '& .MuiAutocomplete-option[aria-selected="true"] .dropdown-option-text, & .MuiAutocomplete-option[aria-selected="true"]:hover .dropdown-option-text, & .MuiAutocomplete-option[aria-selected="true"].Mui-focused .dropdown-option-text, & .MuiAutocomplete-option[aria-selected="true"].Mui-focusVisible .dropdown-option-text':
                  {
                    color: `${colors.DropdownV2?.menuItemSelectedText} !important`,
                  },

                '& .MuiAutocomplete-option .dropdown-option-check': {
                  color: `${colors.DropdownV2?.tickColor} !important`,
                },

                '& .MuiAutocomplete-option[aria-selected="true"] .dropdown-option-check': {
                  color: `${colors.DropdownV2?.tickColor} !important`,
                },

                '& .dropdown-option-check, & .dropdown-option-check svg': {
                  color: `${colors.DropdownV2?.tickColor} !important`,
                },

                '& .dropdown-option-check path': {
                  fill: `${colors.DropdownV2?.tickColor} !important`,
                },
              },
            },
            popupIndicator: {
              sx: {
                color: `${colors.DropdownV2?.dropdownIconActive} !important`,
                '&:hover': {
                  backgroundColor: 'transparent',
                },
                '& svg': {
                  color: `${colors.DropdownV2?.dropdownIconActive} !important`,
                },
                '& svg path': {
                  fill: 'currentColor !important',
                  stroke: 'currentColor !important',
                },
              },
            },
            clearIndicator: {
              sx: {
                color: `${colors.DropdownV2?.menuItemText} !important`,
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              },
            },
          }}
          renderOption={(renderProps, option, { selected }) => (
            <Box
              component="li"
              {...renderProps}
              key={option.value}
              sx={{
                '&.MuiAutocomplete-option': {
                  ...theme.typography.dropdownText,
                },
              }}
            >
              <Box
                sx={{
                  minWidth: 0,
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography
                  className="dropdown-option-text"
                  sx={{
                    ...theme.typography.dropdownListText,
                    color: selected
                      ? `${colors.DropdownV2?.menuItemSelectedText} !important`
                      : `${colors.DropdownV2?.menuItemText} !important`,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {option.label}
                  {option.subLabel ? ` (${option.subLabel})` : ''}
                </Typography>
              </Box>

              {selected ? (
                <CheckIcon
                  className="dropdown-option-check"
                  htmlColor={`${colors.DropdownV2?.tickColor} !important`}
                  sx={{
                    fontSize: '28px',
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                />
              ) : null}
            </Box>
          )}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <TextField
              {...params}
              placeholder={placeholder}
              error={error}
              helperText={helperText}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.DropdownV2?.background,
                  borderRadius: '4px',
                  minHeight: '48px',
                  transition: 'all 0.2s ease-in-out',

                  '& fieldset': {
                    borderColor: value ? `${colors.DropdownV2?.borderColorActive} !important` : '',
                  },

                  '&:hover fieldset': {
                    borderColor: `${colors.DropdownV2?.borderColorHover} !important`,
                  },

                  '&.Mui-focused fieldset': {
                    borderColor: `${colors.DropdownV2?.borderColorActive} !important`,
                    borderWidth: '1px',
                  },
                },

                '& .MuiInputBase-input': {
                  ...theme.typography.dropdownText,
                  color: colors.DropdownV2?.menuItemText,
                  padding: '10px 16px !important',
                  boxSizing: 'border-box',
                },
                '& .MuiInputBase-input::placeholder': {
                  color: colors.DropdownV2?.menuItemText,
                  opacity: 0.5,
                },

                '& .MuiAutocomplete-endAdornment .MuiButtonBase-root': {
                  color: value
                    ? `${colors.DropdownV2?.dropdownIconActive} !important`
                    : `${colors.DropdownV2?.dropdownIconDefault} !important`,
                },

                '& .MuiAutocomplete-popupIndicator': {
                  color: value
                    ? `${colors.DropdownV2?.dropdownIconActive} !important`
                    : `${colors.DropdownV2?.dropdownIconDefault} !important`,
                },

                '& .MuiAutocomplete-popupIndicator svg': {
                  width: '24px',
                  height: '24px',
                  color: value
                    ? `${colors.DropdownV2?.dropdownIconActive} !important`
                    : `${colors.DropdownV2?.dropdownIconDefault} !important`,
                },

                '& .MuiAutocomplete-popupIndicator svg path': {
                  fill: 'currentColor !important',
                  stroke: 'currentColor !important',
                },

                '& .MuiOutlinedInput-root:hover .MuiAutocomplete-endAdornment .MuiButtonBase-root, & .MuiOutlinedInput-root:hover .MuiAutocomplete-popupIndicator, & .MuiOutlinedInput-root:hover .MuiAutocomplete-popupIndicator svg':
                  {
                    color: value
                      ? `${colors.DropdownV2?.dropdownIconActive} !important`
                      : `${colors.DropdownV2?.dropdownIconDefault} !important`,
                  },

                '& .MuiOutlinedInput-root:hover .MuiAutocomplete-popupIndicator svg path': {
                  fill: 'currentColor !important',
                  stroke: 'currentColor !important',
                },

                '& .MuiOutlinedInput-root.Mui-focused .MuiAutocomplete-endAdornment .MuiButtonBase-root, & .MuiOutlinedInput-root.Mui-focused .MuiAutocomplete-popupIndicator, & .MuiOutlinedInput-root.Mui-focused .MuiAutocomplete-popupIndicator svg':
                  {
                    color: `${colors.DropdownV2?.dropdownIconActive} !important`,
                  },

                '& .MuiOutlinedInput-root.Mui-focused .MuiAutocomplete-popupIndicator svg path': {
                  fill: 'currentColor !important',
                  stroke: 'currentColor !important',
                },
              }}
            />
          )}
        />
      </FormControl>
    </Box>
  );
};

export default SearchableDropdown;
